import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Heart, MessageCircle, Share2, Play, Pause, Volume2, VolumeX, Upload, UserPlus, UserCheck, Flag, Bookmark, Video, Mic, X, Eye, CornerDownRight, Languages, DollarSign, ExternalLink, Podcast, MoreVertical, Sparkles, Camera, Users } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import AudioRecorder from './AudioRecorder';
import PodcastUploader from './PodcastUploader';
import PhotoComposer from './photos/PhotoComposer';
import PhotoPostCard from './photos/PhotoPostCard';
import { photoTexts } from './photos/photoTranslations';
import VideoComposer from './videos/VideoComposer';
import SharedVideoCard from './videos/VideoPostCard';
import { videoTexts } from './videos/videoTranslations';
import UserSuggestionsModal, { suggestionTexts } from './UserSuggestionsModal';
import Navigation from './Navigation';
import CommentSection from './CommentSection';
import TranscriptionDisplay from './TranscriptionDisplay';
import ReportModal from './ReportModal';
import AdDisplay from './AdDisplay';
import StoryBar from './StoryBar';
import DiscoverStoriesSection from './DiscoverStoriesSection';
import PromotedProfileCard from './PromotedProfileCard';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { getMediaUrl } from '../utils/mediaUrl';
import { shareContent, buildShareUrl, incrementShareCount } from '../utils/shareContent';
import SponsorPicker from './SponsorPicker';
import SponsorshipBadge from './SponsorshipBadge';
import MonetizationBadge from './MonetizationBadge';
import { renderTitleWithEmoji } from '../utils/renderTitleWithEmoji';
// StudioStoriesPanel removed — audio stories feature deprecated
import MonetizationInfoBubble from './MonetizationInfoBubble';
// StudioStoriesPanel removed — feature deprecated
import PodcastCard from './PodcastCard';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import VerifiedBadge from './VerifiedBadge';
import { usePT } from '../utils/promoteTranslations';

// Emotion badge component (Hume AI)
const EMOTION_META = {
  joy: { emoji: '😊', label: 'Joyeux', color: 'from-yellow-400 to-orange-400' },
  anger: { emoji: '😠', label: 'En colère', color: 'from-red-500 to-orange-600' },
  stress: { emoji: '😰', label: 'Stressé', color: 'from-orange-400 to-red-400' },
  calm: { emoji: '😌', label: 'Calme', color: 'from-blue-400 to-cyan-400' },
  irony: { emoji: '😏', label: 'Ironique', color: 'from-purple-400 to-pink-400' },
  energy: { emoji: '⚡', label: 'Énergique', color: 'from-magenta-400 to-violet-500' },
};

// Compact 13-language catalog for the burger "What do you want to create?" menu
export const BURGER_I18N = {
  fr: { title: 'Que veux-tu créer ?', gimSub: 'Message vocal court (60s max)', podSub: 'Long format audio', avaTitle: 'Voix → avatar animé grâce à Destine IA', avaSub: 'Transformez votre voix en avatar 2D synchronisé (anime, Pixar, Destinemoji, réaliste)' },
  en: { title: 'What do you want to create?', gimSub: 'Short voice message (60s max)', podSub: 'Long-form audio', avaTitle: 'Voice → animated avatar with Destine AI', avaSub: 'Turn your voice into a lip-synced 2D avatar (anime, Pixar, Destinemoji, realistic)' },
  es: { title: '¿Qué quieres crear?', gimSub: 'Mensaje de voz corto (máx 60s)', podSub: 'Audio de larga duración', avaTitle: 'Voz → avatar animado con Destine IA', avaSub: 'Convierte tu voz en un avatar 2D sincronizado (anime, Pixar, Destinemoji, realista)' },
  ar: { title: 'ماذا تريد أن تنشئ؟', gimSub: 'رسالة صوتية قصيرة (60 ثانية كحد أقصى)', podSub: 'صوت بصيغة طويلة', avaTitle: 'صوت → أفاتار متحرك بواسطة Destine IA', avaSub: 'حوّل صوتك إلى أفاتار ثنائي الأبعاد متزامن (أنمي، Pixar، Destinemoji، واقعي)' },
  pt: { title: 'O que você quer criar?', gimSub: 'Mensagem de voz curta (máx 60s)', podSub: 'Áudio em formato longo', avaTitle: 'Voz → avatar animado com Destine IA', avaSub: 'Transforme sua voz em um avatar 2D sincronizado (anime, Pixar, Destinemoji, realista)' },
  de: { title: 'Was möchtest du erstellen?', gimSub: 'Kurze Sprachnachricht (max. 60s)', podSub: 'Langformat-Audio', avaTitle: 'Stimme → animierter Avatar mit Destine IA', avaSub: 'Verwandle deine Stimme in einen lippen-synchronen 2D-Avatar (Anime, Pixar, Destinemoji, realistisch)' },
  ru: { title: 'Что хочешь создать?', gimSub: 'Короткое голосовое сообщение (макс 60с)', podSub: 'Длинный аудиоформат', avaTitle: 'Голос → анимированный аватар с помощью Destine IA', avaSub: 'Превратите свой голос в 2D-аватар с липсинком (аниме, Pixar, Destinemoji, реалистичный)' },
  zh: { title: '你想创建什么？', gimSub: '短语音消息（最长60秒）', podSub: '长音频节目', avaTitle: '声音 → 由 Destine IA 生成的动画头像', avaSub: '将您的声音变成同步的 2D 头像（动漫、Pixar、Destinemoji、写实）' },
  ja: { title: '何を作りたいですか？', gimSub: '短いボイスメッセージ（最大60秒）', podSub: 'ロング形式のオーディオ', avaTitle: '声 → Destine IA によるアニメアバター', avaSub: 'あなたの声を口パク同期の2Dアバターに変換（アニメ、Pixar、Destinemoji、リアル）' },
  hi: { title: 'आप क्या बनाना चाहते हैं?', gimSub: 'छोटा वॉइस मैसेज (अधिकतम 60s)', podSub: 'लंबा ऑडियो फ़ॉर्मैट', avaTitle: 'आवाज़ → Destine IA द्वारा एनिमेटेड अवतार', avaSub: 'अपनी आवाज़ को लिप-सिंक्ड 2D अवतार में बदलें (एनीमे, Pixar, Destinemoji, यथार्थवादी)' },
  nl: { title: 'Wat wil je maken?', gimSub: 'Korte spraakbericht (max 60s)', podSub: 'Audio in lange vorm', avaTitle: 'Stem → geanimeerde avatar via Destine IA', avaSub: 'Verander je stem in een lipgesynchroniseerde 2D-avatar (anime, Pixar, Destinemoji, realistisch)' },
  sw: { title: 'Ungependa kuunda nini?', gimSub: 'Ujumbe mfupi wa sauti (upeo 60s)', podSub: 'Sauti ya muundo mrefu', avaTitle: 'Sauti → avatar iliyoanimishwa kwa Destine IA', avaSub: 'Geuza sauti yako kuwa avatar ya 2D iliyosawazishwa (anime, Pixar, Destinemoji, halisi)' },
  ht: { title: 'Kisa ou vle kreye ?', gimSub: 'Mesaj vokal kout (maks 60s)', podSub: 'Odyo fòma long', avaTitle: 'Vwa → avatar anime ak Destine IA', avaSub: 'Transfòme vwa ou an yon avatar 2D senkwonize (anime, Pixar, Destinemoji, reyalis)' },
};


const EmotionBadge = ({ emotion }) => {
  const meta = EMOTION_META[emotion.top_emotion];
  if (!meta) return null;
  const percent = Math.round(emotion.top_emotion_score || 0);
  return (
    <div
      className={`mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${meta.color} text-white text-sm font-semibold shadow-md`}
      data-testid={`emotion-badge-${emotion.top_emotion}`}
      title="Détecté par l'IA Destine"
    >
      <span className="text-base leading-none">{meta.emoji}</span>
      <span>Vocal {percent}% {meta.label.toLowerCase()}</span>
    </div>
  );
};

const TikTokFeed = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showBurgerMenu, setShowBurgerMenu] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const seenQueue = useRef(new Set());
  const seenSent = useRef(new Set());

  // Remember which posts were really seen (≥60% visible for 1.2 s) → unseen ones come first next time
  useEffect(() => {
    if (!posts.length) return undefined;
    const timers = new Map();
    const flush = () => {
      const ids = [...seenQueue.current];
      if (!ids.length) return;
      seenQueue.current.clear();
      axios.post('/api/feed/seen', { ids }).catch(() => {});
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.dataset.postId;
        if (!id || seenSent.current.has(id)) return;
        if (entry.isIntersecting) {
          if (!timers.has(id)) {
            timers.set(id, setTimeout(() => {
              seenSent.current.add(id);
              seenQueue.current.add(id);
              timers.delete(id);
            }, 1200));
          }
        } else if (timers.has(id)) {
          clearTimeout(timers.get(id));
          timers.delete(id);
        }
      });
    }, { threshold: 0.6 });
    Object.values(postRefs.current).forEach((el) => el && observer.observe(el));
    const interval = setInterval(flush, 2500);
    return () => {
      observer.disconnect();
      timers.forEach((t) => clearTimeout(t));
      clearInterval(interval);
      flush();
    };
  }, [posts]);

  // Shrink the header (hide logo + burger row) once the feed is scrolled
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY || document.documentElement.scrollTop || 0;
        setHeaderCollapsed(prev => (prev ? y > 24 : y > 72));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);
  const [showPodcastUploader, setShowPodcastUploader] = useState(false);
  const [showPhotoComposer, setShowPhotoComposer] = useState(false);
  const [showVideoComposer, setShowVideoComposer] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Fixed header (no sticky lag on iOS) — measure its height to pad the feed
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);
  const [replyToPost, setReplyToPost] = useState(null);
  const [savedPostIds, setSavedPostIds] = useState([]);
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const [promotedUsers, setPromotedUsers] = useState([]); // rotating promo cards
  const [discoverStories, setDiscoverStories] = useState([]); // stories from ALL users (every 7 posts)
  const navigate = useNavigate();
  const postRefs = useRef({});
  const scrolledToPost = useRef(false);
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { t, language } = useLanguage();

  useEffect(() => {
    loadPosts();
    loadSavedPostIds();
    axios.get('/api/stories/discover').then(r => setDiscoverStories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Load a few random promoted profiles to rotate in the feed
  useEffect(() => {
    let cancelled = false;
    const loadPromos = async () => {
      try {
        // Fetch up to 3 distinct promoted users so we can rotate
        const users = [];
        const seen = new Set();
        for (let i = 0; i < 3; i++) {
          try {
            const r = await axios.get('/api/promote-profile/feed-slot');
            const u = r?.data?.promoted_user;
            if (u && !seen.has(u.id)) { users.push(u); seen.add(u.id); }
          } catch (_) { /* silent */ }
        }
        if (!cancelled) setPromotedUsers(users);
      } catch (_) { /* silent */ }
    };
    loadPromos();
    return () => { cancelled = true; };
  }, []);

  // Scroll to specific post (gim toli) when posts are loaded
  useEffect(() => {
    const postId = searchParams.get('post');
    if (postId && posts.length > 0 && !scrolledToPost.current) {
      // Wait for refs to be set
      setTimeout(() => {
        const postElement = postRefs.current[postId];
        if (postElement) {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the post briefly
          postElement.classList.add('ring-2', 'ring-magenta-500');
          setTimeout(() => {
            postElement.classList.remove('ring-2', 'ring-magenta-500');
          }, 2000);
          // Clear the URL param after scrolling
          setSearchParams({});
          scrolledToPost.current = true;
        }
      }, 300);
    }
  }, [posts, searchParams, setSearchParams]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/gim-toli/feed?limit=50');
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedPostIds = async () => {
    try {
      const response = await axios.get('/api/saved-posts/ids');
      setSavedPostIds(response.data);
    } catch (error) {
      console.error('Error loading saved posts:', error);
    }
  };

  const handlePostCreated = () => {
    setShowRecorder(false);
    setReplyToPost(null);
    loadPosts();
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen text-white pb-20 ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
      {/* Header avec Story Bar intégrée — FIXED (pas de sticky : évite le décalage iOS au scroll), collé tout en haut */}
      <div
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-40 border-b border-white/10 ${isDark ? 'bg-black' : ''}`}
        style={!isDark ? { background: 'var(--theme-bg-solid)' } : undefined}
        data-testid="feed-header"
        data-collapsed={headerCollapsed ? 'true' : 'false'}
      >
        {/* Masque l'inset natif iOS (barre d'état) pour que rien ne dépasse au-dessus du header */}
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 pointer-events-none"
          style={{ top: '-200px', height: '201px', background: isDark ? '#000' : 'var(--theme-bg-solid)' }}
        />
        <div className={`container mx-auto px-4 transition-[padding] duration-300 ${headerCollapsed ? 'py-1.5' : 'py-3'}`}>
          {/* Ligne 1: Logo et Burger — glisse hors écran quand on scrolle */}
          <div
            className={`flex items-center justify-between overflow-hidden transition-all duration-300 ease-out ${headerCollapsed ? 'max-h-0 opacity-0 -translate-y-4 mb-0' : 'max-h-16 opacity-100 translate-y-0 mb-2'}`}
            data-testid="feed-header-top-row"
          >
            <img src="/destine-logo.png" alt="Destine" className="h-12 theme-logo" />
            <button
              onClick={() => setShowBurgerMenu(true)}
              className="p-2 hover:scale-110 transition-transform"
              data-testid="create-content-btn"
              title="Créer un contenu"
              tabIndex={headerCollapsed ? -1 : 0}
            >
              <img src="/burger-icon.png" alt="Créer" className="w-10 h-10" />
            </button>
          </div>
          
          {/* Ligne 2: Story Bar */}
          <StoryBar />
        </div>
      </div>

      {/* Feed Content — padded by the fixed header height */}
      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-6" style={{ paddingTop: headerHeight ? `${headerHeight + 16}px` : undefined }} data-testid="feed-content">
        {posts.length === 0 ? (
            <div className="text-center py-20">
              <Mic className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400 mb-4">Aucun Gim Toli pour le moment</p>
              <button
                onClick={() => setShowRecorder(true)}
                className="px-6 py-3 bg-magenta-500 rounded-full hover:bg-magenta-600 transition-colors"
              >
                Créer un Gim Toli
              </button>
            </div>
          ) : (
            posts.map((post, index) => (
              <React.Fragment key={post.id}>
                <div ref={(el) => postRefs.current[post.id] = el} data-post-id={post.id} className="transition-all duration-300 rounded-xl">
                  {post.content_kind === 'podcast' ? (
                    <PodcastCard podcast={post} onUpdate={loadPosts} />
                  ) : post.content_kind === 'photo' ? (
                    <PhotoPostCard post={post} onUpdate={loadPosts} />
                  ) : post.content_kind === 'video' ? (
                    <SharedVideoCard post={post} onUpdate={loadPosts} />
                  ) : (
                    <AudioPostCard 
                      post={post} 
                      onUpdate={loadPosts}
                      initialSaved={savedPostIds.includes(post.id)}
                      onSaveUpdate={loadSavedPostIds}
                      isPlaying={currentPlayingId === post.id}
                      onPlayStateChange={(playing) => setCurrentPlayingId(playing ? post.id : null)}
                      onReply={(post) => { setReplyToPost(post); setShowRecorder(true); }}
                    />
                  )}
                </div>
                {/* Display ad every 5 posts */}
                {(index + 1) % 5 === 0 && (
                  <AdDisplay zone="feed" />
                )}
                {/* Discover stories from every user (same language first) every 7 posts */}
                {discoverStories.length > 0 && (index + 1) % 7 === 0 && (
                  <DiscoverStoriesSection groups={discoverStories} offset={Math.floor(index / 7) * 6} />
                )}
                {/* Display a promoted profile every ~15 posts (rotating) */}
                {promotedUsers.length > 0 && (index + 1) % 15 === 0 && (
                  <PromotedProfileCard
                    promotedUser={promotedUsers[Math.floor(index / 15) % promotedUsers.length]}
                  />
                )}
              </React.Fragment>
            ))
          )}
      </div>

      {/* Audio Recorder Modal */}
      {showRecorder && (
        <AudioRecorder
          onClose={() => { setShowRecorder(false); setReplyToPost(null); }}
          onSuccess={handlePostCreated}
          replyTo={replyToPost}
        />
      )}

      {/* Burger choice modal — pick which content type to create */}
      {showBurgerMenu && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowBurgerMenu(false)}
          data-testid="burger-menu-modal"
        >
          <div
            className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{(BURGER_I18N[language] || BURGER_I18N.en).title}</h3>
              <button onClick={() => setShowBurgerMenu(false)} className="p-2 hover:bg-white/10 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Voice → Animated Avatar CTA (full width, above Gim Toli + Podcast) */}
            <button
              onClick={() => { setShowBurgerMenu(false); navigate('/studio'); }}
              className="text-white w-full mb-3 rounded-2xl bg-gradient-to-br from-fuchsia-600 via-violet-700 to-purple-900 p-5 text-left border border-white/10 hover:scale-[1.01] active:scale-100 transition-transform shadow-lg shadow-fuchsia-900/30"
              data-keep-dark="true" data-testid="burger-choice-avatar-ia"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base leading-tight">{(BURGER_I18N[language] || BURGER_I18N.en).avaTitle}</p>
                  <p className="text-xs text-white/80 mt-1">{(BURGER_I18N[language] || BURGER_I18N.en).avaSub}</p>
                </div>
              </div>
            </button>
            {/* Ajouter des amis — suggestions (même langue, actifs, aléatoire) — entre Avatar IA et Vidéo */}
            <button
              onClick={() => { setShowBurgerMenu(false); setShowSuggestions(true); }}
              className="text-white w-full mb-3 rounded-2xl bg-gradient-to-br from-yellow-500 via-orange-500 to-fuchsia-600 p-5 text-left border border-white/10 hover:scale-[1.01] active:scale-100 transition-transform"
              data-keep-dark="true" data-testid="burger-choice-suggestions"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base leading-tight">{suggestionTexts(language).burgerTitle}</p>
                  <p className="text-xs text-white/80 mt-1">{suggestionTexts(language).burgerSub}</p>
                </div>
              </div>
            </button>
            {/* Vidéo partagée — sous l'avatar IA, au-dessus de Gim Toli / Podcast */}
            <button
              onClick={() => { setShowBurgerMenu(false); setShowVideoComposer(true); }}
              className="text-white w-full mb-3 rounded-2xl bg-gradient-to-br from-cyan-500 via-sky-600 to-fuchsia-700 p-5 text-left border border-white/10 hover:scale-[1.01] active:scale-100 transition-transform"
              data-keep-dark="true" data-testid="burger-choice-video"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base leading-tight">{videoTexts(language).burgerTitle}</p>
                  <p className="text-xs text-white/80 mt-1">{videoTexts(language).burgerSub}</p>
                </div>
              </div>
            </button>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setShowBurgerMenu(false); setShowRecorder(true); }}
                className="text-white group rounded-2xl bg-gradient-to-br from-magenta-500 to-violet-600 p-5 text-left hover:scale-[1.02] transition-transform"
                data-keep-dark="true" data-testid="burger-choice-gim-toli"
              >
                <Mic className="w-8 h-8 text-white mb-3" />
                <p className="font-bold">Gim Toli</p>
                <p className="text-xs text-white/80 mt-1">{(BURGER_I18N[language] || BURGER_I18N.en).gimSub}</p>
              </button>
              <button
                onClick={() => { setShowBurgerMenu(false); setShowPodcastUploader(true); }}
                className="text-white group rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 p-5 text-left hover:scale-[1.02] transition-transform"
                data-keep-dark="true" data-testid="burger-choice-podcast"
              >
                <Podcast className="w-8 h-8 text-white mb-3" />
                <p className="font-bold">Podcast</p>
                <p className="text-xs text-white/80 mt-1">{(BURGER_I18N[language] || BURGER_I18N.en).podSub}</p>
              </button>
              <button
                onClick={() => { setShowBurgerMenu(false); setShowPhotoComposer(true); }}
                className="text-white group col-span-2 rounded-2xl bg-gradient-to-br from-pink-500 via-fuchsia-600 to-purple-700 p-5 text-left hover:scale-[1.02] transition-transform"
                data-keep-dark="true" data-testid="burger-choice-photos"
              >
                <Camera className="w-8 h-8 text-white mb-3" />
                <p className="font-bold">{photoTexts(language).burgerTitle}</p>
                <p className="text-xs text-white/80 mt-1">{photoTexts(language).burgerSub}</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Podcast uploader modal — opened from the burger */}
      {showPodcastUploader && (
        <PodcastUploader
          onClose={() => setShowPodcastUploader(false)}
          onSuccess={() => { setShowPodcastUploader(false); loadPosts(); toast.success('Podcast publié avec succès !'); }}
        />
      )}

      {/* Photo composer — Instagram-style photo sharing opened from the burger */}
      {showPhotoComposer && (
        <PhotoComposer onClose={() => setShowPhotoComposer(false)} onSuccess={loadPosts} />
      )}
      {showVideoComposer && (
        <VideoComposer onClose={() => setShowVideoComposer(false)} onSuccess={loadPosts} />
      )}
      {showSuggestions && <UserSuggestionsModal onClose={() => setShowSuggestions(false)} />}

      <Navigation />
    </div>
  );
};

const AudioPostCard = ({ post, onUpdate, initialSaved = false, onSaveUpdate, isPlaying = false, onPlayStateChange, onReply }) => {
  const { user } = useAuth();
  const { language: postLanguage } = useLanguage();
  const kbHeight = useKeyboardHeight();
  const ptFeed = usePT();
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(initialSaved);
  const [showComments, setShowComments] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showKebab, setShowKebab] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showSponsorPicker, setShowSponsorPicker] = useState(false);
  const [sharesCount, setSharesCount] = useState(post.shares_count || 0);
  const [showTranslateLang, setShowTranslateLang] = useState(false);
  const [translatingGim, setTranslatingGim] = useState(false);
  const [gimTranslation, setGimTranslation] = useState(null);
  const audioRef = useRef(null);
  const navigate = useNavigate();

  // Resolve the real follow state on mount (fixes the "button stays purple even after subscribing" bug)
  useEffect(() => {
    let cancelled = false;
    if (!user || !post?.user_id || user.id === post.user_id) return;
    (async () => {
      try {
        const r = await axios.get(`/api/users/${post.user_id}`);
        if (!cancelled) setIsFollowing((r.data?.followers || []).includes(user.id));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user, post?.user_id]);

  const TRANSLATION_LANGS = [
    { code: 'fr', label: '🇫🇷 Français' }, { code: 'en', label: '🇬🇧 Anglais' },
    { code: 'es', label: '🇪🇸 Espagnol' }, { code: 'pt', label: '🇵🇹 Portugais' },
    { code: 'de', label: '🇩🇪 Allemand' }, { code: 'ar', label: '🇸🇦 Arabe' },
    { code: 'zh', label: '🇨🇳 Chinois' }, { code: 'ja', label: '🇯🇵 Japonais' },
    { code: 'hi', label: '🇮🇳 Hindi' }, { code: 'ru', label: '🇷🇺 Russe' },
    { code: 'nl', label: '🇳🇱 Néerlandais' }, { code: 'sw', label: '🇰🇪 Swahili' },
    { code: 'ht', label: '🇭🇹 Kreyòl ayisyen' },
  ];

  const handleTranslateGimToli = async (targetLang) => {
    setShowTranslateLang(false);
    setTranslatingGim(true);
    try {
      const audioUrl = post.audio_url?.startsWith('/api') ? post.audio_url : `/api${post.audio_url}`;
      const fd = new FormData();
      fd.append('audio_url', audioUrl);
      fd.append('target_lang', targetLang);
      const res = await axios.post('/api/translate-voice-by-url', fd);
      setGimTranslation(res.data);
    } catch (e) {
      toast.error('Erreur de traduction');
    } finally {
      setTranslatingGim(false);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      if (onPlayStateChange) {
        onPlayStateChange(false);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // No auto-play in card mode

  const togglePlay = () => {
    if (onPlayStateChange) {
      onPlayStateChange(!isPlaying);
    }
  };
  
  // Effect to play/pause audio based on isPlaying prop
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Reset if ended
      if (audio.ended) {
        audio.currentTime = 0;
      }
      audio.play().catch(err => console.error('Play error:', err));
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(!audio.muted);
  };

  const handleLike = async () => {
    try {
      const response = await axios.post(`/api/gim-toli/${post.id}/like`);
      setLiked(response.data.liked);
      onUpdate();
    } catch (error) {
      console.error('Like error:', error);
      toast.error('Erreur lors du like');
    }
  };

  const handleFollow = async () => {
    // Optimistic flip so the user sees feedback instantly.
    const next = !isFollowing;
    setIsFollowing(next);
    try {
      const r = await axios.post(`/api/users/${post.user_id}/follow`);
      // Server is the source of truth — trust its value to recover from race conditions.
      if (typeof r.data?.is_following === 'boolean') setIsFollowing(r.data.is_following);
      toast.success(next ? 'Suivi avec succès' : 'Ne suit plus');
    } catch (error) {
      setIsFollowing(!next); // rollback
      console.error('Follow error:', error);
      toast.error('Erreur lors du suivi');
    }
  };

  const handleSave = async () => {
    try {
      const response = await axios.post(`/api/gim-toli/${post.id}/save`);
      setSaved(response.data.saved);
      toast.success(response.data.saved ? 'Post enregistré !' : 'Post retiré des enregistrements');
      
      // Call the callback to update saved posts list if provided
      if (onSaveUpdate) {
        onSaveUpdate();
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleShare = async () => {
    setShowShareMenu(true);
  };

  const handleCopyLink = async () => {
    try {
      const title = post.title || 'Gim Toli';
      const result = await shareContent({
        title,
        text: `${title} — Écoute ça sur Destine !`,
        url: buildShareUrl('gim_toli', post.id),
      });
      if (result.shared && !result.cancelled) {
        const newCount = await incrementShareCount('gim_toli', post.id);
        if (typeof newCount === 'number') setSharesCount(newCount);
      }
      setShowShareMenu(false);
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Erreur lors du partage');
    }
  };

  const handleShareToStory = async () => {
    try {
      await axios.post('/api/stories/share-content', {
        content_id: post.id,
        content_type: 'gim_toli',
        text_overlay: post.title || 'Écoutez ce Gim Toli !'
      });
      // Also increment the shares counter so it stays in sync with native share path.
      const newCount = await incrementShareCount('gim_toli', post.id);
      if (typeof newCount === 'number') setSharesCount(newCount);
      toast.success(ptFeed('story.shared'));
      setShowShareMenu(false);
    } catch (error) {
      console.error('Share to story error:', error);
      toast.error(ptFeed('story.shareError'));
    }
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = (clickX / rect.width) * 100;
    const newTime = (percentage / 100) * audioRef.current.duration;
    
    audioRef.current.currentTime = newTime;
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Generate waveform bars for voice message visualization
  const waveformBars = Array.from({ length: 40 }, (_, i) => {
    // Create a pseudo-random but consistent pattern based on index
    const height = 20 + Math.sin(i * 0.5) * 15 + Math.cos(i * 0.3) * 10 + (i % 3) * 5;
    return Math.max(15, Math.min(45, height));
  });

  return (
    <div
      className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden hover:border-magenta-500/50 hover:shadow-2xl hover:shadow-magenta-500/20 transition-all duration-300"
      data-testid="audio-post-card"
    >
      {/* Optional user-uploaded background image */}
      {post.background_image_url && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-70 pointer-events-none"
            style={{ backgroundImage: `url(${getMediaUrl(post.background_image_url)})` }}
            data-testid="feed-post-background-image"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50 pointer-events-none" aria-hidden="true" />
        </>
      )}
      <div className={post.background_image_url ? 'relative z-10' : ''} {...(post.background_image_url ? { 'data-keep-dark': '' } : {})}>
      {/* Reply indicator - if this post is a reply */}
      {post.reply_to && (
        <div className="px-4 pt-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-violet-500/10 border border-violet-500/15 rounded-xl text-xs">
            <CornerDownRight className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
            <span className="text-violet-400">En réponse à</span>
            <span className="text-white font-medium truncate">{post.reply_to.user?.name || 'un utilisateur'}</span>
            {post.reply_to.title && <span className="text-gray-400 truncate">— {post.reply_to.title}</span>}
          </div>
        </div>
      )}

      {/* Voice Message Style Player */}
      <div className="p-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-violet-900/40 to-magenta-900/40 rounded-2xl border border-white/10">
          {/* Play/Pause Button - Small circular */}
          <button
            onClick={togglePlay}
            className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full hover:from-magenta-600 hover:to-violet-700 hover:scale-105 transition-all shadow-lg"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" fill="white" />
            ) : (
              <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
            )}
          </button>

          {/* Waveform and Progress */}
          <div className="flex-1 min-w-0">
            {/* Waveform Visualization */}
            <div 
              className="relative h-10 flex items-center gap-[2px] cursor-pointer"
              onClick={handleProgressClick}
            >
              {waveformBars.map((height, index) => {
                const barProgress = (index / waveformBars.length) * 100;
                const isPlayed = barProgress <= progress;
                return (
                  <div
                    key={index}
                    className={`flex-1 rounded-full transition-all duration-100 ${
                      isPlayed 
                        ? 'bg-gradient-to-t from-magenta-500 to-violet-500' 
                        : 'bg-white/20'
                    }`}
                    style={{ 
                      height: `${height}%`,
                      opacity: isPlayed ? 1 : 0.5
                    }}
                  />
                );
              })}
            </div>

            {/* Time Display */}
            <div className="flex justify-between items-center mt-1 text-xs text-gray-400">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Volume Control */}
          <button
            onClick={toggleMute}
            className="flex-shrink-0 p-2 hover:bg-white/10 rounded-full transition-all"
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5 text-gray-400" />
            ) : (
              <Volume2 className="w-5 h-5 text-magenta-400" />
            )}
          </button>
        </div>
      </div>

      {/* Cover Image - Optional, smaller */}
      {post.cover_image_url && (
        <div className="px-4 pb-2">
          <div className="relative aspect-video rounded-xl overflow-hidden">
            <img
              src={getMediaUrl(post.cover_image_url)}
              alt="Cover"
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('Cover image error:', post.cover_image_url);
                e.target.style.display = 'none';
              }}
            />
          </div>
        </div>
      )}

      {/* Audio (hidden) */}
      <audio
        ref={audioRef}
        src={getMediaUrl(post.audio_url?.startsWith('/api') ? post.audio_url : `/api${post.audio_url}`)}
        preload="metadata"
        onError={(e) => console.error('Audio load error:', post.audio_url)}
      />

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* User Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Avatar — if the user has an active story, wrap the circle in a
                gradient ring; clicking opens the story viewer. Otherwise the
                click navigates to their profile as before. */}
            {post.user?.has_active_story ? (
              <button
                onClick={() => navigate(`/feed?story_user=${post.user_id}`)}
                className="relative p-[2px] rounded-full bg-gradient-to-tr from-magenta-500 via-pink-500 to-amber-400"
                title="Voir la story"
                data-testid={`feed-avatar-story-${post.user_id}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center overflow-hidden border-2 border-black">
                  {post.user?.profile_image_url ? (
                    <img src={getMediaUrl(post.user.profile_image_url)} alt={post.user.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xl font-bold">{post.user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                  )}
                </div>
              </button>
            ) : (
              <div 
                className="w-14 h-14 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center ring-2 ring-white/10 group-hover:ring-magenta-500/50 transition-all cursor-pointer overflow-hidden"
                onClick={() => navigate(`/profile/${post.user_id}`)}
              >
                {post.user?.profile_image_url ? (
                  <img
                    src={getMediaUrl(post.user.profile_image_url)}
                    alt={post.user.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Profile image error:', post.user.profile_image_url);
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <span className="text-xl font-bold">
                    {post.user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                )}
              </div>
            )}
            <div>
              <h3 
                className="font-semibold text-white text-lg cursor-pointer hover:text-magenta-400 transition-colors"
                onClick={() => navigate(`/profile/${post.user_id}`)}
              >
                {post.user?.name || 'Utilisateur'}
                {post.user?.is_verified && <VerifiedBadge size="sm" className="ml-1" />}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {(() => {
                  const localeMap = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', ar: 'ar-SA', pt: 'pt-PT', de: 'de-DE', it: 'it-IT', ru: 'ru-RU', zh: 'zh-CN', ja: 'ja-JP', hi: 'hi-IN', nl: 'nl-NL', sw: 'sw-KE', ht: 'ht-HT' };
                  const loc = localeMap[postLanguage] || 'fr-FR';
                  const d = new Date(post.created_at);
                  return d.toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })
                    + ' · '
                    + d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
                })()}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Follow Button — green when followed, magenta→violet gradient when not */}
            {user && user.id !== post.user_id && (
              <button
                onClick={handleFollow}
                data-testid={`gimtoli-follow-${post.id}`}
                className={`flex items-center space-x-1 px-3 py-2 rounded-full font-semibold transition-all text-sm sm:px-4 ${
                  isFollowing
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-gradient-to-r from-magenta-500 to-violet-600 text-white hover:from-magenta-600 hover:to-violet-700'
                }`}
              >
                {isFollowing ? (
                  <UserCheck className="w-4 h-4" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{isFollowing ? 'Suivi' : 'Suivre'}</span>
              </button>
            )}
            
            {/* Volume Control */}
            <button
              onClick={toggleMute}
              className="p-2 sm:p-3 hover:bg-white/10 rounded-full transition-all hover:scale-110 flex-shrink-0"
            >
              {isMuted ? (
                <VolumeX className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
              ) : (
                <Volume2 className="w-5 h-5 sm:w-6 sm:h-6 text-magenta-400" />
              )}
            </button>
          </div>
        </div>

        {/* Title */}
        {post.title && (
          <h2 className="text-2xl font-bold mb-4">
            {renderTitleWithEmoji(post.title, 'bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent')}
          </h2>
        )}

        {/* Sponsorship mention */}
        {post.sponsored_by?.sponsor_name && (
          <SponsorshipBadge sponsorship={post.sponsored_by} contentKind="gim_toli" />
        )}

        {/* Emotion badge (Hume AI) */}
        {post.emotion_analysis?.top_emotion && post.emotion_analysis?.top_emotion_score > 30 && (
          <EmotionBadge emotion={post.emotion_analysis} />
        )}

        {/* Transcription */}
        {post.transcription_words && post.transcription_words.length > 0 && (
          <div className="mb-4">
            <TranscriptionDisplay
              words={post.transcription_words}
              currentTime={currentTime}
              isPlaying={isPlaying}
            />
          </div>
        )}

        {/* Translate button */}
        {post.audio_url && (
          <button
            onClick={() => setShowTranslateLang(true)}
            className="mb-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-xs font-medium transition-all"
            data-testid={`translate-gim-toli-${post.id}`}
            disabled={translatingGim}
          >
            <Languages className="w-3.5 h-3.5" />
            {translatingGim ? ptFeed('feed.translating') : ptFeed('feed.translateVoice')}
          </button>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-white/5">
          <button
            onClick={handleLike}
            className="flex items-center space-x-2 px-4 py-2 rounded-full hover:bg-magenta-500/10 transition-all group"
          >
            <Heart 
              className={`w-6 h-6 transition-all ${liked ? 'text-magenta-500 scale-110' : 'text-gray-400 group-hover:text-magenta-500'}`}
              fill={liked ? 'currentColor' : 'none'} 
            />
            <span className="font-semibold text-lg">{post.likes_count || 0}</span>
          </button>

          <button 
            onClick={() => setShowComments(!showComments)}
            className="flex items-center space-x-2 px-4 py-2 rounded-full hover:bg-violet-500/10 transition-all group"
          >
            <MessageCircle className="w-6 h-6 text-gray-400 group-hover:text-violet-500 transition-colors" />
            <span className="font-semibold text-lg">{post.comments_count || 0}</span>
          </button>

          {user && onReply && (
            <button
              onClick={() => onReply(post)}
              className="flex items-center space-x-2 px-4 py-2 rounded-full hover:bg-cyan-500/10 transition-all group"
              title="Répondre avec un Gim Toli"
              data-testid="reply-gimtoli-btn"
            >
              <CornerDownRight className="w-5 h-5 text-gray-400 group-hover:text-cyan-400 transition-colors" />
            </button>
          )}

          <div className="relative">
            <button 
              onClick={handleShare}
              className="flex items-center space-x-2 px-4 py-2 rounded-full hover:bg-blue-500/10 transition-all group"
              data-testid={`gim-toli-share-toggle-${post.id}`}
              aria-label="Partager"
            >
              <Share2 className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors" />
              <span className="font-semibold text-lg" data-testid={`gim-toli-shares-count-${post.id}`}>{sharesCount}</span>
            </button>
            
            {/* Share Menu Dropdown */}
            {showShareMenu && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-gray-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                <button
                  onClick={handleCopyLink}
                  className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center space-x-3"
                  data-testid="gim-toli-share-button"
                >
                  <Share2 className="w-5 h-5 text-blue-400" />
                  <span>{ptFeed('feed.share')}</span>
                </button>
                {user && (
                  <button
                    onClick={handleShareToStory}
                    className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center space-x-3 border-t border-white/10"
                    data-testid="gim-toli-share-to-story"
                  >
                    <Heart className="w-5 h-5 text-magenta-400" />
                    <span>{ptFeed('story.shareGimToStory')}</span>
                  </button>
                )}
                {user && user.id === post.user_id && (
                  <button
                    onClick={() => { setShowShareMenu(false); setShowSponsorPicker(true); }}
                    className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center space-x-3 border-t border-white/10"
                    data-testid="gim-toli-share-with-sponsor"
                  >
                    <DollarSign className="w-5 h-5 text-emerald-400" />
                    <span>{ptFeed('feed.shareWithSponsor')}</span>
                  </button>
                )}
                <button
                  onClick={() => setShowShareMenu(false)}
                  className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors text-gray-400 border-t border-white/10"
                >
                  {ptFeed('feed.cancel')}
                </button>
              </div>
            )}
          </div>

          {/* Kebab menu (3 dots) — groups Save + Report so they don't overflow
              the screen on narrow phones. */}
          {user && (
            <div className="relative">
              <button
                onClick={() => setShowKebab(!showKebab)}
                className="p-2 rounded-full hover:bg-white/10 transition-all"
                title={ptFeed('feed.moreOptions')}
                data-testid={`kebab-gimtoli-${post.id}`}
              >
                <MoreVertical className="w-5 h-5 text-gray-400" />
              </button>
              {showKebab && (
                <div
                  className="absolute right-0 bottom-12 w-56 bg-gray-900 border border-white/10 rounded-xl shadow-2xl z-30 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`kebab-menu-${post.id}`}
                >
                  <button
                    onClick={() => { setShowKebab(false); handleSave(); }}
                    className="w-full px-4 py-3 text-left hover:bg-white/5 text-sm flex items-center gap-3"
                  >
                    <Bookmark className={`w-4 h-4 ${saved ? 'text-yellow-500 fill-current' : ''}`} />
                    {saved ? ptFeed('feed.unsave') : ptFeed('feed.save')}
                  </button>
                  <button
                    onClick={() => { setShowKebab(false); setShowReportModal(true); }}
                    className="w-full px-4 py-3 text-left hover:bg-white/5 text-sm text-red-300 flex items-center gap-3 border-t border-white/10"
                  >
                    <Flag className="w-4 h-4" /> {ptFeed('feed.report')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Comments Section */}
        {showComments && (
          <CommentsDrawer onClose={() => setShowComments(false)} kbHeight={kbHeight} title={ptFeed('comments.title')}>
            <CommentSection postId={post.id} />
          </CommentsDrawer>
        )}

      </div>
      
      {/* Report Modal - Rendered outside card to avoid z-index issues */}
      {showReportModal && (
        <ReportModal
          contentType="gim_toli"
          contentId={post.id}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {/* Translate language picker */}
      {showTranslateLang && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowTranslateLang(false)} data-testid="gim-translate-picker">
          <div className="bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 border border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Languages className="w-5 h-5 text-cyan-400" />
                Traduire en…
              </h3>
              <button onClick={() => setShowTranslateLang(false)} className="p-1.5 hover:bg-white/10 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {TRANSLATION_LANGS.map(l => (
                <button
                  key={l.code}
                  onClick={() => handleTranslateGimToli(l.code)}
                  className="px-3 py-3 bg-white/5 hover:bg-cyan-500/20 border border-white/10 hover:border-cyan-500/40 rounded-xl text-sm font-medium transition-all"
                  data-testid={`gim-translate-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Translation result */}
      {gimTranslation && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[9999] flex items-center justify-center p-4" onClick={() => setGimTranslation(null)}>
          <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-white/10" onClick={(e) => e.stopPropagation()} data-testid="gim-translation-result">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Languages className="w-5 h-5 text-cyan-400" />
                Traduction
              </h3>
              <button onClick={() => setGimTranslation(null)} className="p-1.5 hover:bg-white/10 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400 mb-1">Original</p>
                <p className="text-sm bg-white/5 p-3 rounded-lg">{gimTranslation.original_text}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Traduction ({gimTranslation.target_lang})</p>
                <p className="text-sm bg-cyan-500/10 border border-cyan-500/30 p-3 rounded-lg">{gimTranslation.translated_text}</p>
              </div>
              {gimTranslation.audio_base64 && (
                <audio src={`data:audio/mpeg;base64,${gimTranslation.audio_base64}`} controls autoPlay className="w-full" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sponsor picker modal (post owner only) */}
      <SponsorPicker
        open={showSponsorPicker}
        onClose={() => setShowSponsorPicker(false)}
        contentId={post.id}
        contentKind="gim_toli"
        onSubmitted={() => toast.success('Demande envoyée au sponsor')}
        onShareDirect={handleCopyLink}
      />
      </div>
    </div>
  );
};

// ============= VIDEO POST CARD =============
const VideoPostCard = ({ video, onUpdate }) => {
  const { user } = useAuth();
  const ptFeed = usePT();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(video.likes?.length || 0);
  const [sharesCount, setSharesCount] = useState(video.shares_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [showSponsorPicker, setShowSponsorPicker] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showInlinePlayer, setShowInlinePlayer] = useState(false);
  const [viewRecorded, setViewRecorded] = useState(false);
  const [saved, setSaved] = useState(false);
  const viewTimerRef = useRef(null);
  const navigate = useNavigate();
  const isNativePlatform = Capacitor.isNativePlatform();

  useEffect(() => {
    if (user && video.likes) {
      setLiked(video.likes.includes(user.id));
    }
    checkFollowStatus();
    checkIfSaved();
  }, [user, video]);

  const checkIfSaved = async () => {
    if (!user) return;
    try {
      const res = await axios.get('/api/saved-posts/ids');
      if (res.data?.includes(video.id)) setSaved(true);
    } catch (e) {}
  };

  const handleSaveVideo = async () => {
    if (!user) { toast.error('Connectez-vous pour enregistrer'); return; }
    try {
      const res = await axios.post(`/api/videos/${video.id}/save`);
      setSaved(res.data.saved);
      toast.success(res.data.saved ? 'Court-métrage enregistré !' : 'Retiré des enregistrements');
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const checkFollowStatus = async () => {
    if (!user || user.id === video.user_id) return;
    try {
      const response = await axios.get(`/api/users/${video.user_id}`);
      setIsFollowing(response.data.followers?.includes(user.id) || false);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Connectez-vous pour liker');
      return;
    }
    try {
      const response = await axios.post(`/api/explorer/videos/${video.id}/like`);
      setLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
    } catch (error) {
      toast.error('Erreur lors du like');
    }
  };

  const handleFollow = async () => {
    if (!user) {
      toast.error('Connectez-vous pour suivre');
      return;
    }
    try {
      const response = await axios.post(`/api/users/${video.user_id}/follow`);
      setIsFollowing(response.data.is_following);
      toast.success(response.data.is_following ? 'Vous suivez maintenant cet utilisateur' : 'Vous ne suivez plus cet utilisateur');
    } catch (error) {
      toast.error('Erreur lors du follow');
    }
  };

  const handleWatchVideo = async () => {
    const url = video.video_url;
    
    // Check if it's a YouTube video
    const youtubeMatch = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    
    if (youtubeMatch && isNativePlatform) {
      try {
        await Browser.open({ 
          url: url.includes('youtube.com') || url.includes('youtu.be') ? url : `https://www.youtube.com/watch?v=${youtubeMatch[1]}`,
          presentationStyle: 'fullscreen',
          toolbarColor: '#000000'
        });
      } catch (error) {
        window.open(url, '_blank');
      }
    } else if (youtubeMatch) {
      window.open(url, '_blank');
    } else if (url?.startsWith('/api/')) {
      // Local video - open inline player
      setShowInlinePlayer(true);
    } else {
      window.open(url, '_blank');
    }
  };

  // Record view after 5s of playback (unique per user)
  const handleVideoPlaying = () => {
    if (viewRecorded) return;
    viewTimerRef.current = setTimeout(async () => {
      try {
        await axios.post(`/api/explorer/videos/${video.id}/view`);
        setViewRecorded(true);
      } catch (e) {}
    }, 5000);
  };

  const handleVideoPause = () => {
    if (viewTimerRef.current) {
      clearTimeout(viewTimerRef.current);
      viewTimerRef.current = null;
    }
  };

  const loadComments = async () => {
    try {
      const response = await axios.get(`/api/explorer/videos/${video.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    try {
      const response = await axios.post(`/api/explorer/videos/${video.id}/comments`, {
        content: commentText
      });
      setComments([response.data, ...comments]);
      setCommentText('');
      toast.success('Commentaire ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleShare = async () => {
    const title = video.title || 'Court-métrage';
    try {
      const result = await shareContent({
        title,
        text: `${title} — Regarde ça sur Destine !`,
        url: buildShareUrl('short', video.id),
      });
      if (result.shared && !result.cancelled) {
        const newCount = await incrementShareCount('short', video.id);
        if (typeof newCount === 'number') setSharesCount(newCount);
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Erreur lors du partage');
    } finally {
      setShowShareMenu(false);
    }
  };

  // Get YouTube thumbnail if it's a YouTube video
  const getYouTubeThumbnail = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
    }
    return null;
  };

  const thumbnailUrl = video.thumbnail_url 
    ? getMediaUrl(video.thumbnail_url) 
    : getYouTubeThumbnail(video.video_url) || '/video-placeholder.png';

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all">
      {/* Video Player / Thumbnail */}
      <div className="relative aspect-video cursor-pointer group">
        {showInlinePlayer && video.video_url?.startsWith('/api/') ? (
          <div className="relative w-full h-full bg-black">
            <video
              src={getMediaUrl(video.video_url)}
              className="w-full h-full object-contain"
              controls
              autoPlay
              playsInline
              onPlay={handleVideoPlaying}
              onPause={handleVideoPause}
              onEnded={handleVideoPause}
              data-testid="video-inline-player"
            />
            <button
              onClick={() => { setShowInlinePlayer(false); handleVideoPause(); }}
              className="absolute top-2 right-2 p-2 bg-black/60 rounded-full hover:bg-black/80 z-10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div onClick={handleWatchVideo}>
            <img
              src={thumbnailUrl}
              alt={video.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = '/video-placeholder.png'; }}
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
              <div className="w-16 h-16 bg-cyan-500/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
            </div>
            {video.is_youtube && (
              <div className="absolute top-3 right-3 px-2 py-1 bg-red-600/90 rounded text-xs font-medium">
                YouTube
              </div>
            )}
          </div>
        )}
        {/* Views */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/60 px-2 py-1 rounded text-xs">
          <Eye className="w-3 h-3" />
          {video.views || 0} vues
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* User info */}
        <div className="flex items-center justify-between mb-3">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate(`/profile/${video.user_id}`)}
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center overflow-hidden">
              {video.user?.profile_image_url ? (
                <img src={getMediaUrl(video.user.profile_image_url)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-bold">{video.user?.name?.[0]?.toUpperCase()}</span>
              )}
            </div>
            <div>
              <p className="font-semibold text-sm flex items-center gap-1">
                {video.user?.name}
                {video.user?.is_verified && <VerifiedBadge size="xs" />}
              </p>
              <p className="text-xs text-gray-400">@{video.user?.username}</p>
            </div>
          </div>
          
          {user && user.id !== video.user_id && (
            <button
              onClick={handleFollow}
              data-testid={`video-follow-${video.id}`}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                isFollowing
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                  : 'bg-cyan-500 text-white hover:bg-cyan-600'
              }`}
            >
              {isFollowing ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Title */}
        <h3 className="font-semibold mb-2 line-clamp-2">{video.title}</h3>
        {video.sponsored_by?.sponsor_name && (
          <SponsorshipBadge sponsorship={video.sponsored_by} contentKind="video" compact />
        )}
        {video.description && (
          <p className="text-sm text-gray-400 mb-3 line-clamp-2">{video.description}</p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-white/10 pt-3 mt-3">
          <button 
            onClick={handleLike}
            className={`flex items-center gap-2 ${liked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'} transition-colors`}
          >
            <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
            <span className="text-sm">{likesCount}</span>
          </button>

          <button 
            onClick={() => { setShowComments(!showComments); if (!showComments) loadComments(); }}
            className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm">{video.comments_count || 0}</span>
          </button>

          <button 
            onClick={handleShare}
            className="flex items-center gap-2 text-gray-400 hover:text-green-400 transition-colors"
            data-testid="short-video-share-btn"
            aria-label="Partager"
          >
            <Share2 className="w-5 h-5" />
            <span className="text-sm">{sharesCount}</span>
          </button>

          {/* v60.6 — badge monétisation toujours visible (motivation + explication 13 langues) */}
          <MonetizationBadge sponsored={!!video.sponsored_by?.sponsor_name} />

          {user && user.id === video.user_id && (
            <button
              onClick={() => setShowSponsorPicker(true)}
              className="flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors"
              data-testid="short-video-sponsor-btn"
              aria-label="Partager avec sponsor"
              title="Partager avec sponsor"
            >
              <DollarSign className="w-5 h-5" />
            </button>
          )}

          <button 
            onClick={handleSaveVideo}
            className={`flex items-center gap-2 ${saved ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'} transition-colors`}
            title={saved ? 'Retirer des enregistrements' : 'Enregistrer'}
            data-testid="save-video-btn"
          >
            <Bookmark className={`w-5 h-5 ${saved ? 'fill-current' : ''}`} />
          </button>

          <button 
            onClick={() => setShowReportModal(true)}
            className="text-gray-400 hover:text-orange-400 transition-colors"
          >
            <Flag className="w-5 h-5" />
          </button>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 pt-4 border-t border-white/10">
            {user && (
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={ptFeed('comments.addPh') || 'Ajouter un commentaire…'}
                  className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-cyan-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleComment()}
                />
                <button
                  onClick={handleComment}
                  className="px-4 py-2 bg-cyan-500 rounded-full text-sm font-medium hover:bg-cyan-600 transition-colors"
                >
                  Envoyer
                </button>
              </div>
            )}
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {comments.map(comment => (
                <div key={comment.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {comment.user?.profile_image_url ? (
                      <img src={getMediaUrl(comment.user.profile_image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold">{comment.user?.name?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm"><span className="font-semibold">{comment.user?.name}</span>{comment.user?.is_verified && <VerifiedBadge size="xs" className="ml-1" />}</p>
                    <p className="text-sm text-gray-300">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showReportModal && (
        <ReportModal
          contentType="video"
          contentId={video.id}
          onClose={() => setShowReportModal(false)}
        />
      )}

      <SponsorPicker
        open={showSponsorPicker}
        onClose={() => setShowSponsorPicker(false)}
        contentId={video.id}
        contentKind="video"
        onSubmitted={() => toast.success('Demande envoyée au sponsor')}
        onShareDirect={handleShare}
      />
    </div>
  );
};

// ============= VIDEO UPLOADER MODAL =============
const VideoUploader = ({ onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 500MB for now)
      if (file.size > 500 * 1024 * 1024) {
        toast.error('La vidéo ne doit pas dépasser 500 Mo');
        return;
      }
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnail(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!videoFile) {
      toast.error('La vidéo est requise');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('video', videoFile);
      if (thumbnail) {
        formData.append('thumbnail', thumbnail);
      }

      await axios.post('/api/explorer/videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min timeout for large video uploads
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success('Court-métrage publié avec succès !');
      onSuccess();
    } catch (error) {
      console.error('Upload error:', error);
      // If the bytes were fully sent (progress 100%) but the response was
      // cut by an intermediate proxy / network, the server most likely
      // finished processing in background. Show a neutral info toast
      // instead of a misleading error.
      const fullySent = uploadProgress >= 100;
      const isNetwork = !error?.response && (
        error?.code === 'ECONNABORTED' ||
        error?.code === 'ERR_NETWORK' ||
        error?.message?.toLowerCase().includes('network') ||
        error?.message?.toLowerCase().includes('timeout')
      );
      if (fullySent && isNetwork) {
        toast.info('Publication en cours côté serveur — votre court-métrage apparaîtra dans quelques instants.');
        onSuccess();
      } else {
        toast.error('Erreur lors de la publication');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto pt-4 pb-20 px-4">
      <div className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-lg">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-white/10 p-4 flex items-center justify-between z-10 rounded-t-2xl">
          <h2 className="text-xl font-bold">{t("messages.publish")}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-4 pb-8">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">Titre *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("messages.titleField")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-cyan-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décrivez votre court-métrage..."
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-cyan-500 resize-none"
            />
          </div>

          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium mb-2">Vidéo * (max 60 min, 500 Mo)</label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              className="hidden"
              id="video-input"
            />
            <label
              htmlFor="video-input"
              className="block w-full p-4 border-2 border-dashed border-white/20 rounded-xl text-center cursor-pointer hover:border-cyan-500/50 transition-colors"
            >
              {videoPreview ? (
                <div className="space-y-2">
                  <video 
                    src={videoPreview} 
                    className="w-full aspect-video object-cover rounded-lg"
                    controls
                  />
                  <p className="text-sm text-cyan-400">{videoFile?.name}</p>
                </div>
              ) : (
                <div className="text-gray-400 py-8">
                  <Video className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">Cliquez pour sélectionner une vidéo</p>
                  <p className="text-xs text-gray-500 mt-1">MP4, MOV, AVI... (max 500 Mo)</p>
                </div>
              )}
            </label>
          </div>

          {/* Custom Thumbnail */}
          <div>
            <label className="block text-sm font-medium mb-2">Miniature (optionnel)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="hidden"
              id="thumbnail-input"
            />
            <label
              htmlFor="thumbnail-input"
              className="block w-full p-4 border-2 border-dashed border-white/20 rounded-xl text-center cursor-pointer hover:border-cyan-500/50 transition-colors"
            >
              {thumbnailPreview ? (
                <img src={thumbnailPreview} alt="Thumbnail" className="w-full aspect-video object-cover rounded-lg" />
              ) : (
                <div className="text-gray-400 py-4">
                  <p className="text-sm">Ajouter une miniature personnalisée</p>
                </div>
              )}
            </label>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="w-full bg-white/10 rounded-full h-2">
                <div 
                  className="bg-cyan-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-center text-gray-400">
                Upload en cours... {uploadProgress}%
              </p>
            </div>
          )}

          <MonetizationInfoBubble />

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={uploading || !title.trim() || !videoFile}
            className={`w-full py-3 rounded-xl font-medium transition-colors ${
              uploading || !title.trim() || !videoFile
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-cyan-500 hover:bg-cyan-600 text-white'
            }`}
          >
            {uploading ? '...' : t('messages.publish')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TikTokFeed;

// =====================================================================
// CommentsDrawer — bottom-sheet wrapper that lifts the dialog above the
// on-screen keyboard on iOS/Android (Capacitor) and keeps it scrollable.
// =====================================================================
function CommentsDrawer({ onClose, kbHeight = 0, children, title }) {
  return (
    <div className="fixed inset-0 z-[120] bg-black/85 flex items-end justify-center p-0" onClick={onClose} data-testid="gim-comments-drawer">
      <div
        className="w-full max-w-md bg-gradient-to-br from-violet-900 to-fuchsia-900 rounded-t-3xl border border-white/10 flex flex-col"
        style={{
          transform: kbHeight ? `translateY(-${kbHeight}px)` : undefined,
          transition: 'transform 200ms ease-out',
          maxHeight: kbHeight ? `calc(85vh - ${kbHeight}px)` : '85vh',
          paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-bold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> {title || 'Commentaires'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="gim-comments-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </div>
  );
}
